import type { LocationQuery } from 'vue-router'
import type {
  BrandFilters,
  OtherFilters,
  PriceRangeFilter,
  TypeFilters,
} from '~/types/api/data/product/filter/filter.types'
import type {
  SelectedFilterParams,
  SelectedPriceRangeFilter,
  SelectedTypeFilters,
} from '~/types/stores/product-listing/filters-store.types'

export const SORTED_QUERY_KEYS_TO_KEEP = [
  'language',
  'sort',
  'brands',
  'min-price',
  'max-price',
  'others',
  'q',
]

export const getQueriesFromRoute = (routeQueries: LocationQuery) => {
  const query: { [key: string]: string } = {}
  if (Object.keys(routeQueries).length > 0) {
    SORTED_QUERY_KEYS_TO_KEEP.forEach((key: string) => {
      if (routeQueries[key]) {
        query[key] = routeQueries[key].toString()
      }
    })
  }
  return query
}

export const getQueriesFromSelectedFilters = (
  selectedFilters: SelectedFilterParams
) => {
  const query: { [key: string]: string | undefined } = {}
  query['brands'] = selectedFilters.brands.length
    ? [...selectedFilters.brands].sort().join(',')
    : undefined

  Object.entries(selectedFilters.priceRange).forEach(([key, value]) => {
    query[key] = value ? value : undefined
  })

  query['others'] = selectedFilters.others.length
    ? [...selectedFilters.others].sort().join(',')
    : undefined

  Object.entries(selectedFilters.types).forEach(([key, value]) => {
    query[key] = value.length ? [...value].sort().join(',') : undefined
  })

  return query
}

export const getQueriesInPreDefinedFormat = (query: {
  [key: string]: string | undefined
}) => {
  return {
    language: query.language,
    sort: query.sort,
    brands: query.brands,
    'min-price': query['min-price'],
    'max-price': query['max-price'],
    others: query.others,
    q: query.q,
  }
}

export const isFilterObjectEmpty = (
  obj: SelectedTypeFilters | SelectedPriceRangeFilter
) => {
  let objectEmpty = true
  Object.values(obj).forEach(value => {
    if (objectEmpty && value.length > 0) {
      objectEmpty = false
    }
  })
  return objectEmpty
}

export const isFilterParamsAvailable = (
  selectedFilterParams: SelectedFilterParams
) => {
  const { brands, priceRange, types, others } = selectedFilterParams
  return (
    brands.length ||
    others.length ||
    !isFilterObjectEmpty(types) ||
    !isFilterObjectEmpty(priceRange)
  )
}

export const getValidatedCurrentTypeFilters = (
  data: TypeFilters[],
  selectedTypes: SelectedTypeFilters
): SelectedTypeFilters => {
  return data.reduce((obj: SelectedTypeFilters, filterType) => {
    if (selectedTypes[filterType.slugUrl]) {
      obj[filterType.slugUrl] = selectedTypes[filterType.slugUrl]
    } else {
      obj[filterType.slugUrl] = []
    }
    return obj
  }, {})
}

export const getValidatedCurrentFilters = (
  data: BrandFilters[] | OtherFilters[],
  currentFilters: string[]
): string[] => {
  const trackingHashMap = new Map()
  currentFilters.forEach(slugUrl => {
    trackingHashMap.set(slugUrl, true)
  })
  return data
    .filter(state => trackingHashMap.has(state.slugUrl))
    .map(state => state.slugUrl)
}

export const getValidatedCurrentPriceRange = (
  data: PriceRangeFilter,
  selectedPriceRange: SelectedPriceRangeFilter
): SelectedPriceRangeFilter => {
  const minPrice = selectedPriceRange['min-price']
  const maxPrice = selectedPriceRange['max-price']
  const priceRange = {
    'min-price': '',
    'max-price': '',
  }
  if (+minPrice > data.minPrice && +minPrice < data.maxPrice) {
    priceRange['min-price'] = minPrice
  }
  if (+maxPrice > data.minPrice && +maxPrice < data.maxPrice) {
    priceRange['max-price'] = maxPrice
  }
  return priceRange
}
